import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { uploadDocument } from "~/lib/document-upload";
import { DocumentType, SalesRequestDocument } from "~/types/client";
import { type Database } from "~/types/supabase";
import { resolveGross } from "~/utils/iva";

// Type for sales_request_documents table
type SalesRequestDocumentRow =
  Database["public"]["Tables"]["sales_request_documents"]["Row"];
type SalesRequestDocumentInsert =
  Database["public"]["Tables"]["sales_request_documents"]["Insert"];

// Bucket name for storing sales request files
const BUCKET_NAME = "sales-request-files";
// Prefix for temporary document IDs
const TEMP_PREFIX = "temp_";

/**
 * Function to map database record to frontend SalesRequestDocument
 */
function mapToSalesRequestDocument(
  doc: SalesRequestDocumentRow
): SalesRequestDocument {
  return {
    id: doc.id,
    salesRequestId: doc.sales_request_id,
    documentType: doc.document_type as DocumentType,
    type: doc.document_type as DocumentType, // Keep for backward compatibility
    fileName: doc.filename,
    fileUrl: doc.document_url,
    fileType: doc.content_type,
    fileSize: doc.file_size,
    orderNumber: doc.order_number,
    documentDate: doc.document_date,
    createdAt: doc.created_at,
    createdBy: doc.uploaded_by,
    updatedAt: doc.updated_at,
    isTemporary: doc.is_temporary,
    cancelled: doc.cancelled,
    cancellationDate: (doc as any).cancellation_date,
    creditNoteId: (doc as any).credit_note_id,
    creditNoteNumber: (doc as any).credit_note_number,
    bsaleNumber: doc.bsale_number,
    deleted_at: (doc as any).deleted_at,
  };
}

/**
 * Generates a valid UUID v4 string
 * This is a simple implementation that doesn't require external dependencies
 */
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Validates a file for upload as a sales request document
 */
export const validateFile = (
  file: File
): { valid: boolean; message?: string } => {
  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      message: "El archivo no debe superar los 5MB.",
    };
  }

  // Validate file type
  const validTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  ];

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      message:
        "Formato no válido. Por favor seleccione una imagen, PDF o documento de Office.",
    };
  }

  return { valid: true };
};

/**
 * Uploads a document for a sales request
 */
export const uploadSalesRequestDocument = async (
  file: File,
  salesRequestId: string,
  documentType: DocumentType,
  userId: string,
  orderNumber?: string,
  documentDate?: string
): Promise<SalesRequestDocument | null> => {
  console.time("Document Upload");
  console.log(
    `[Document Upload] Starting upload process for file: ${file.name}`
  );
  console.log(
    `[Document Upload] File size: ${file.size} bytes, type: ${file.type}`
  );
  console.log(`[Document Upload] Sales request ID: ${salesRequestId}`);
  console.log(`[Document Upload] Document type: ${documentType}`);
  if (documentType === "orden_de_compra" && orderNumber) {
    console.log(`[Document Upload] Order number: ${orderNumber}`);
  }
  if (documentType === "orden_de_compra" && documentDate) {
    console.log(`[Document Upload] Document date: ${documentDate}`);
  }

  try {
    // Validate file
    console.log("[Document Upload] Validating file...");
    const validation = validateFile(file);
    if (!validation.valid) {
      console.log(`[Document Upload] Validation failed: ${validation.message}`);
      toast({
        title: "Archivo inválido",
        description: validation.message,
        variant: "destructive",
      });
      return null;
    }

    console.log(
      `[Document Upload] File validation passed. Uploading to storage...`
    );

    // Use the document-upload utility to upload the file and create the database record
    const result = await uploadDocument({
      file,
      salesRequestId,
      documentType,
      orderNumber,
      documentDate,
      userId,
    });

    if (!result.success) {
      console.error(
        "[Document Upload] Error uploading document:",
        result.error
      );
      throw new Error(result.error || "Failed to upload document");
    }

    console.log(
      `[Document Upload] Document uploaded successfully. ID: ${result.documentId}`
    );

    // Transform to frontend format
    const document: SalesRequestDocument = {
      id: result.documentId!,
      salesRequestId: salesRequestId,
      documentType: documentType,
      type: documentType,
      fileName: result.fileName!,
      fileUrl: result.fileUrl!,
      fileType: result.fileType!,
      fileSize: result.fileSize!,
      orderNumber: orderNumber,
      documentDate: documentDate,
      createdAt: new Date().toISOString(),
      createdBy: userId,
      updatedAt: null,
    };

    console.log(
      `[Document Upload] Document upload process completed successfully`
    );
    console.timeEnd("Document Upload");

    if (result.usedFallback) {
      // Show a note about the fallback
      toast({
        title: "Documento registrado con respaldo",
        description:
          "El documento se ha registrado con una imagen de respaldo debido a problemas de conexión con el almacenamiento.",
      });
    } else {
      toast({
        title: "Documento cargado exitosamente",
        description: "El documento ha sido subido correctamente.",
      });
    }

    return document;
  } catch (error) {
    console.error(
      "[Document Upload] Error in uploadSalesRequestDocument:",
      error
    );
    console.timeEnd("Document Upload");

    // Provide more specific error messages
    let errorMessage = "Ha ocurrido un error al subir el documento.";

    if (error instanceof Error) {
      // Provide specific error messages for common issues
      if (
        error.message.includes("bucket not found") ||
        error.message.includes("Bucket not found")
      ) {
        errorMessage = `Error de almacenamiento: No se pudo encontrar el bucket "${BUCKET_NAME}". Por favor contacte al administrador.`;
      } else if (error.message.includes("permission denied")) {
        errorMessage =
          "No tiene permisos para subir documentos. Por favor contacte al administrador.";
      } else if (error.message.includes("JWT")) {
        errorMessage =
          "La sesión ha expirado. Por favor inicie sesión nuevamente.";
      } else {
        errorMessage = error.message;
      }
    }

    toast({
      title: "Error al subir el documento",
      description: errorMessage,
      variant: "destructive",
    });
    return null;
  }
};

/**
 * Fetches documents for a sales request
 */
export const fetchSalesRequestDocuments = async (
  salesRequestId: string
): Promise<SalesRequestDocument[]> => {
  try {
    const { data, error } = await supabase
      .from("sales_request_documents")
      .select("*")
      .eq("sales_request_id", salesRequestId)
      .is("deleted_at", null) // Exclude soft-deleted documents
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching documents:", error);
      return [];
    }

    // Transform to frontend format using our mapping function
    const documents: SalesRequestDocument[] = data.map((rawDoc) => {
      const document = mapToSalesRequestDocument(rawDoc);
      // Add client ID and price_with_iva from the joined sales_request table
      document.clientId = (rawDoc as any).sales_request?.client_id;
      // Add price_with_iva to use for amount display
      (
        document as SalesRequestDocument & { price_with_iva?: number }
      ).price_with_iva = (rawDoc as any).sales_request?.price_with_iva;
      return document;
    });

    return documents;
  } catch (error) {
    console.error("Error in fetchSalesRequestDocuments:", error);
    return [];
  }
};

/**
 * Deletes a sales request document
 */
export const deleteSalesRequestDocument = async (
  documentId: string
): Promise<boolean> => {
  try {
    console.log(`[Document Delete] Deleting document ID: ${documentId}`);

    // First get the document to get the file path and check if it's a proof of delivery
    const { data: document, error: fetchError } = await supabase
      .from("sales_request_documents")
      .select("*")
      .eq("id", documentId)
      .single();

    if (fetchError) {
      console.error(
        "[Document Delete] Error fetching document to delete:",
        fetchError
      );
      return false;
    }

    // Check if this is a proof of delivery document and get sales request status
    let shouldRevertStatus = false;
    let salesRequestId = null;

    if (
      document &&
      document.document_type === "proof_of_delivery" &&
      document.sales_request_id
    ) {
      salesRequestId = document.sales_request_id;

      // Get current sales request status
      const { data: salesRequest, error: salesRequestError } = await supabase
        .from("sales_request")
        .select("status")
        .eq("id", salesRequestId)
        .single();

      if (
        !salesRequestError &&
        salesRequest &&
        salesRequest.status === "delivered"
      ) {
        shouldRevertStatus = true;
        console.log(
          `[Document Delete] Will revert status from delivered to in_transit for sales request ${salesRequestId}`
        );
      }
    }

    // If this is a real file (not a fallback/placeholder), try to delete it from storage
    if (
      document &&
      document.document_url &&
      !document.filename.startsWith("[OFFLINE]")
    ) {
      // Extract the file path from the URL
      try {
        const url = new URL(document.document_url);
        const pathParts = url.pathname.split("/");
        const fileName = pathParts[pathParts.length - 1];

        if (fileName) {
          console.log(
            `[Document Delete] Attempting to delete file from storage: ${fileName}`
          );

          // Delete from storage
          const { error: storageError } = await supabase.storage
            .from(BUCKET_NAME)
            .remove([fileName]);

          if (storageError) {
            console.warn(
              "[Document Delete] Error deleting file from storage (continuing anyway):",
              storageError
            );
          } else {
            console.log(
              "[Document Delete] File deleted from storage successfully"
            );
          }
        }
      } catch (parseError) {
        console.warn(
          "[Document Delete] Could not parse file URL, skipping storage deletion:",
          parseError
        );
      }
    }

    // If this is a factura with a bsale_number, delete corresponding bi_transactions entries
    if (
      document &&
      document.document_type === "factura" &&
      document.bsale_number
    ) {
      console.log(
        `[Document Delete] Deleting bi_transactions entries for bsale_number: ${document.bsale_number}`
      );

      const { error: biTransactionsError } = await supabase
        .from("bi_transactions" as any)
        .delete()
        .eq("nro_factura", document.bsale_number);

      if (biTransactionsError) {
        console.error(
          "[Document Delete] Error deleting bi_transactions entries:",
          biTransactionsError
        );
        // Continue with document deletion even if bi_transactions deletion fails
      } else {
        console.log(
          `[Document Delete] bi_transactions entries deleted for bsale_number: ${document.bsale_number}`
        );
      }
    }

    // Delete the database record
    const { error: deleteError } = await supabase
      .from("sales_request_documents")
      .delete()
      .eq("id", documentId);

    if (deleteError) {
      console.error(
        "[Document Delete] Error deleting document record:",
        deleteError
      );
      return false;
    }

    // If this was a proof of delivery and status should be reverted, update the sales request
    if (shouldRevertStatus && salesRequestId) {
      console.log(
        `[Document Delete] Reverting sales request ${salesRequestId} status from delivered to in_transit`
      );

      // Update sales request status back to in_transit
      const { error: statusError } = await supabase
        .from("sales_request")
        .update({
          status: "in_transit",
          updated_at: new Date().toISOString(),
        })
        .eq("id", salesRequestId);

      if (statusError) {
        console.error(
          "[Document Delete] Error reverting sales request status:",
          statusError
        );
        // Don't fail the whole operation, just log the error
      } else {
        // Add status history entry for the reversion
        const { error: historyError } = await supabase
          .from("sales_request_status_history")
          .insert({
            sales_request_id: salesRequestId,
            old_status: "delivered",
            new_status: "in_transit",
            changed_by: document.uploaded_by, // Use the original uploader as the changed_by
            changed_at: new Date().toISOString(),
          });

        if (historyError) {
          console.error(
            "[Document Delete] Error adding status history:",
            historyError
          );
        } else {
          console.log(
            `[Document Delete] Status reverted and history updated for sales request ${salesRequestId}`
          );
        }
      }
    }

    console.log(`[Document Delete] Document record deleted successfully`);
    return true;
  } catch (error) {
    console.error(
      "[Document Delete] Error in deleteSalesRequestDocument:",
      error
    );
    toast({
      title: "Error al eliminar el documento",
      description:
        error instanceof Error
          ? error.message
          : "Ha ocurrido un error al eliminar el documento.",
      variant: "destructive",
    });
    return false;
  }
};

/**
 * Uploads a document immediately without a sales request ID
 * This allows files to be uploaded before the sales request is created
 */
export const uploadTemporaryDocument = async (
  file: File,
  documentType: DocumentType,
  userId: string,
  orderNumber?: string,
  documentDate?: string
): Promise<SalesRequestDocument | null> => {
  console.time("Temporary Document Upload");
  console.log(
    `[Temp Document Upload] Starting upload process for file: ${file.name}`
  );

  try {
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      toast({
        title: "Archivo inválido",
        description: validation.message,
        variant: "destructive",
      });
      return null;
    }

    // Upload the file to storage bucket first
    const timestamp = new Date().getTime();
    const filePath = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;

    // Upload file to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      console.error(
        "[Temp Document Upload] Storage upload error:",
        uploadError
      );
      throw new Error(`Failed to upload file: ${uploadError.message}`);
    }

    // Get public URL for the file
    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(uploadData.path);

    if (!urlData || !urlData.publicUrl) {
      throw new Error("Failed to get file URL");
    }

    // Create document record - let the database generate the UUID automatically
    const { data: docData, error: docError } = await supabase
      .from("sales_request_documents")
      .insert({
        sales_request_id: null, // Will be updated later
        document_type: documentType,
        filename: file.name,
        document_url: urlData.publicUrl,
        content_type: file.type,
        file_size: file.size,
        order_number: orderNumber,
        document_date: documentDate,
        uploaded_by: userId,
        created_at: new Date().toISOString(),
        is_temporary: true, // Mark as temporary
      })
      .select()
      .single();

    if (docError) {
      console.error("[Temp Document Upload] Database error:", docError);
      throw new Error(`Failed to create document record: ${docError.message}`);
    }

    console.log(
      `[Temp Document Upload] Completed successfully. ID: ${docData.id}`
    );
    console.timeEnd("Temporary Document Upload");

    // Return formatted document using our mapping function
    return mapToSalesRequestDocument(docData);
  } catch (error) {
    console.error("[Temp Document Upload] Error:", error);
    console.timeEnd("Temporary Document Upload");

    let errorMessage = "Ha ocurrido un error al subir el documento.";
    if (error instanceof Error) {
      errorMessage = error.message;
    }

    toast({
      title: "Error al subir el documento",
      description: errorMessage,
      variant: "destructive",
    });
    return null;
  }
};

/**
 * Links temporary documents to a sales request by updating their sales_request_id
 */
export const linkDocumentsToSalesRequest = async (
  tempDocuments: SalesRequestDocument[],
  salesRequestId: string
): Promise<boolean> => {
  if (!tempDocuments || tempDocuments.length === 0) {
    console.log("linkDocumentsToSalesRequest: No documents provided");
    return true; // No documents to link
  }

  console.log(
    `Linking ${tempDocuments.length} documents to sales request ${salesRequestId}`
  );

  try {
    // Debug the documents received
    console.log(
      "Documents received for linking:",
      JSON.stringify(tempDocuments, null, 2)
    );

    // Check for documents that have valid IDs (they exist in the database)
    // We need to filter out any malformed documents or those without a proper ID
    const tempsToLink = tempDocuments.filter(
      (doc) =>
        doc &&
        doc.id &&
        typeof doc.id === "string" &&
        (doc.id.toString().startsWith(TEMP_PREFIX) || doc.isTemporary === true)
    );

    console.log(
      "Documents filtered for linking:",
      JSON.stringify(tempsToLink, null, 2)
    );

    if (tempsToLink.length === 0) {
      console.log("No temporary documents to link after filtering");
      return true; // No temporary documents to link
    }

    // Update each temporary document with the sales request ID
    let successCount = 0;
    for (const doc of tempsToLink) {
      try {
        console.log(
          `Attempting to link document ${doc.id} to sales request ${salesRequestId}`
        );

        const { error } = await supabase
          .from("sales_request_documents")
          .update({
            sales_request_id: salesRequestId,
            is_temporary: false,
          })
          .eq("id", doc.id);

        if (error) {
          console.error(`Error linking document ${doc.id}:`, error);
          // Continue with other documents even if one fails
        } else {
          console.log(
            `Document ${doc.id} linked successfully to sales request ${salesRequestId}`
          );
          successCount++;
        }
      } catch (err) {
        console.error(`Exception when linking document ${doc.id}:`, err);
      }
    }

    // Return true only if all documents were linked successfully
    console.log(
      `Linked ${successCount} out of ${tempsToLink.length} documents`
    );
    return successCount === tempsToLink.length;
  } catch (error) {
    console.error("Error linking documents to sales request:", error);
    return false;
  }
};

/**
 * Fetches all factura documents with optional date filtering and seller filtering
 */
export const fetchAllFacturas = async (
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string
): Promise<SalesRequestDocument[]> => {
  try {
    // Apply date filters validation first
    const hasDateFilters =
      (startDate && startDate instanceof Date && !isNaN(startDate.getTime())) ||
      (endDate && endDate instanceof Date && !isNaN(endDate.getTime()));

    let query = supabase
      .from("sales_request_documents")
      .select(
        `*,
         sales_request:sales_request_id(
           id,
           client_id,
           total_amount,
           price_with_iva,
           status,
           paid,
           paid_amount,
           deleted_at,
           payment_due_date,
           factura_creation_date,
           sales_condition_id,
           created_by,
           sales_conditions:sales_condition_id(name),
           clients:client_id(
             id,
             name,
             is_test,
             sales_condition_id,
             sales_conditions:sales_condition_id(name)
           )
         )`
      )
      .or(
        hasDateFilters
          ? "document_type.eq.factura,type.eq.factura,document_type.eq.nota_de_credito,document_type.eq.nota_de_debito"
          : "document_type.eq.factura,type.eq.factura"
      )
      .is("deleted_at", null) // Exclude soft-deleted documents
      .order("bsale_number", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });

    // Log the query for debugging
    console.log(
      `🔍 Data Service Query - Date filters: ${hasDateFilters}, Start: ${startDate?.toISOString()}, End: ${endDate?.toISOString()}`
    );
    if (hasDateFilters && startDate && endDate) {
      const isJuly2025OrLater =
        (startDate.getFullYear() === 2025 && startDate.getMonth() >= 6) ||
        startDate.getFullYear() > 2025;
    }

    // Apply date filters if provided with proper validation
    // Note: Cancelled documents are now always included for complete historical reporting

    if (startDate && startDate instanceof Date && !isNaN(startDate.getTime())) {
      const startDateString = startDate.toISOString();
      query = query.gte("created_at", startDateString);
    }

    if (endDate && endDate instanceof Date && !isNaN(endDate.getTime())) {
      // Set time to end of day for end date
      const endOfDay = new Date(endDate.getTime()); // Use getTime() for safer date copying
      endOfDay.setHours(23, 59, 59, 999);
      const endDateString = endOfDay.toISOString();
      query = query.lte("created_at", endDateString);
    }

    // Apply seller filtering for sellers - they can only see facturas from sales requests they created
    let sellerRequestIds: string[] = [];
    console.log(
      `🔍 SELLER FILTER DEBUG: userRole=${userRole}, currentUserId=${currentUserId}`
    );

    let allData: any[] = [];

    if (userRole === "seller" && currentUserId) {
      console.log(`🔒 APPLYING SELLER FILTERING for user: ${currentUserId}`);
      // First get all sales_request IDs created by this seller
      // Exclude BSale Directo documents (is_temporary = true) and documents from inactive sellers
      const { data: sellerRequests, error: sellerError } = await supabase
        .from("sales_request")
        .select(
          `
          id,
          created_by,
          profiles:created_by(is_active)
        `
        )
        .eq("created_by", currentUserId);

      if (sellerError) {
        console.error("Error fetching seller's sales requests:", sellerError);
        return [];
      }

      // Filter out requests from inactive sellers (though this shouldn't happen for current seller)
      const validSellerRequests =
        sellerRequests?.filter((req) => {
          const profile = Array.isArray(req.profiles)
            ? req.profiles[0]
            : req.profiles;
          return profile?.is_active !== false;
        }) || [];

      sellerRequestIds = validSellerRequests.map((req) => req.id) || [];
      console.log(
        `🔍 SELLER REQUESTS FOUND: ${sellerRequestIds.length} requests for seller ${currentUserId}`
      );

      if (sellerRequestIds.length === 0) {
        console.log(`❌ NO SELLER REQUESTS FOUND - returning empty array`);
        return [];
      }

      // Process seller requests in batches to avoid URL length limits
      const batchSize = 100; // Process in batches of 100 to avoid long URLs
      console.log(
        `🔒 PROCESSING SELLER REQUESTS IN BATCHES OF ${batchSize} (total: ${sellerRequestIds.length})`
      );

      for (let i = 0; i < sellerRequestIds.length; i += batchSize) {
        const batch = sellerRequestIds.slice(i, i + batchSize);
        console.log(
          `🔍 Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(sellerRequestIds.length / batchSize)} (${batch.length} requests)`
        );

        let batchQuery = supabase
          .from("sales_request_documents")
          .select(
            `*,
             sales_request:sales_request_id(
               id,
               client_id,
               total_amount,
               price_with_iva,
               status,
               paid,
               paid_amount,
               deleted_at,
               payment_due_date,
               factura_creation_date,
               sales_condition_id,
               created_by,
               sales_conditions:sales_condition_id(name),
               clients:client_id(
                 id,
                 name,
                 is_test,
                 sales_condition_id,
                 sales_conditions:sales_condition_id(name)
               )
             )`
          )
          .or(
            hasDateFilters
              ? "document_type.eq.factura,type.eq.factura,document_type.eq.nota_de_credito,document_type.eq.nota_de_debito"
              : "document_type.eq.factura,type.eq.factura"
          )
          .is("deleted_at", null)
          .in("sales_request_id", batch)
          .eq("is_temporary", false) // Exclude BSale Directo documents
          .order("bsale_number", { ascending: false, nullsFirst: false })
          .order("created_at", { ascending: false });

        // Apply date filters to batch query
        if (
          startDate &&
          startDate instanceof Date &&
          !isNaN(startDate.getTime())
        ) {
          const startDateString = startDate.toISOString();
          batchQuery = batchQuery.gte("created_at", startDateString);
        }

        if (endDate && endDate instanceof Date && !isNaN(endDate.getTime())) {
          const endOfDay = new Date(endDate.getTime());
          endOfDay.setHours(23, 59, 59, 999);
          const endDateString = endOfDay.toISOString();
          batchQuery = batchQuery.lte("created_at", endDateString);
        }

        const { data: batchData, error: batchError } = await batchQuery;

        if (batchError) {
          console.error(
            `Error fetching batch ${Math.floor(i / batchSize) + 1}:`,
            batchError
          );
          continue; // Continue with next batch instead of failing completely
        }

        if (batchData && batchData.length > 0) {
          console.log(
            `✅ Batch ${Math.floor(i / batchSize) + 1} returned ${batchData.length} documents`
          );
          allData = [...allData, ...batchData];
        }
      }

      console.log(`🔍 TOTAL DOCUMENTS FROM ALL BATCHES: ${allData.length}`);
    } else {
      // For non-seller users, execute single query as before
      if (userRole !== "superadmin") {
        query = query.eq("is_temporary", false);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching factura documents:", error);
        return [];
      }

      allData = data || [];
    }

    console.log(
      `🔍 RAW QUERY RESULTS: ${allData?.length || 0} documents returned`
    );
    if (userRole === "seller" && allData) {
      // Debug: Check for problematic documents
      const bsaleDirectoCount = allData.filter(
        (doc) => doc.is_temporary === true
      ).length;
      const noSalesRequestCount = allData.filter(
        (doc) => !doc.sales_request_id
      ).length;
      const creditNotesCount = allData.filter(
        (doc) => doc.document_type === "nota_de_credito"
      ).length;

      console.log(`🔍 SELLER DEBUG - BSale Directo docs: ${bsaleDirectoCount}`);
      console.log(
        `🔍 SELLER DEBUG - Docs without sales_request: ${noSalesRequestCount}`
      );
      console.log(`🔍 SELLER DEBUG - Credit notes: ${creditNotesCount}`);

      // Log specific problematic documents
      const problematicDocs = allData.filter(
        (doc) =>
          doc.is_temporary === true ||
          !doc.sales_request_id ||
          (doc.document_type === "nota_de_credito" && !doc.sales_request_id)
      );

      if (problematicDocs.length > 0) {
        console.log(
          `⚠️ PROBLEMATIC DOCUMENTS FOUND:`,
          problematicDocs.map((doc) => ({
            bsale_number: doc.bsale_number,
            document_type: doc.document_type,
            is_temporary: doc.is_temporary,
            sales_request_id: doc.sales_request_id,
          }))
        );
      }
    }

    // Add special facturas for May 2025 reporting if filtering for that period
    // Note: allData is already initialized above
    if (hasDateFilters && startDate && endDate) {
      const rangeIncludes2025 =
        startDate.getFullYear() === 2025 && endDate.getFullYear() === 2025;

      const rangeIncludesMay2025 =
        rangeIncludes2025 &&
        startDate.getMonth() <= 4 &&
        endDate.getMonth() >= 4; // May is month 4

      const rangeIncludesJune2025 =
        rangeIncludes2025 &&
        startDate.getMonth() <= 5 &&
        endDate.getMonth() >= 5; // June is month 5

      if (rangeIncludesMay2025) {
        // Check which special facturas are already included
        const includedBsaleNumbers = new Set(
          allData.map((doc) => doc.bsale_number?.toString()).filter(Boolean)
        );

        const specialNumbers = ["232104", "231827"];
        const missingNumbers = specialNumbers.filter(
          (num) => !includedBsaleNumbers.has(num)
        );

        if (missingNumbers.length > 0) {
          // Fetch the missing special facturas
          let specialQuery = supabase
            .from("sales_request_documents")
            .select(
              `*,
               sales_request:sales_request_id(
                 id,
                 client_id,
                 total_amount,
                 price_with_iva,
                 status,
                 paid,
                 paid_amount,
                 deleted_at,
                 payment_due_date,
                 factura_creation_date,
                 sales_condition_id,
                 created_by,
                 sales_conditions:sales_condition_id(name)
               )`
            )
            .in("bsale_number", missingNumbers)
            .eq("cancelled", true)
            .is("deleted_at", null);

          // Apply seller filtering to special facturas as well
          if (
            userRole === "seller" &&
            currentUserId &&
            sellerRequestIds.length > 0
          ) {
            // For special facturas, batch the seller request IDs if needed
            if (sellerRequestIds.length <= 100) {
              specialQuery = specialQuery
                .in("sales_request_id", sellerRequestIds)
                .eq("is_temporary", false); // Exclude BSale Directo documents
            } else {
              // For large seller lists, we need to handle this differently
              // Since we're filtering by specific bsale_numbers first, we can fetch all matching docs
              // and then filter by seller on the client side
              specialQuery = specialQuery.eq("is_temporary", false);
            }
          } else if (userRole !== "superadmin") {
            // For non-seller users, still exclude BSale Directo documents
            specialQuery = specialQuery.eq("is_temporary", false);
          }

          const { data: specialFacturasRaw, error: specialError } =
            await specialQuery
              .order("bsale_number", { ascending: false, nullsFirst: false })
              .order("created_at", { ascending: false });

          // Filter by seller on client side if we couldn't do it in the query
          let specialFacturas = specialFacturasRaw;
          if (
            userRole === "seller" &&
            currentUserId &&
            sellerRequestIds.length > 100 &&
            specialFacturasRaw
          ) {
            const sellerRequestIdsSet = new Set(sellerRequestIds);
            specialFacturas = specialFacturasRaw.filter((doc) =>
              sellerRequestIdsSet.has(doc.sales_request_id)
            );
            console.log(
              `🔍 Special facturas filtered by seller: ${specialFacturasRaw.length} -> ${specialFacturas.length}`
            );
          }

          if (!specialError && specialFacturas && specialFacturas.length > 0) {
            console.log(
              `✅ Found ${specialFacturas.length} special facturas (cancelled + SII-declared) to include for reporting`
            );
            allData = [...allData, ...specialFacturas];
          } else if (specialError) {
            console.error("❌ Error fetching special facturas:", specialError);
          } else {
            console.log(
              "⚠️ No special facturas (cancelled + SII-declared) found in database"
            );
          }
        } else {
          console.log("✅ All special facturas already included");
        }
      } else if (rangeIncludesJune2025) {
        // For June 2025, include ALL facturas and credit notes (no hard-coded filtering)
        console.log(
          "🔍 June 2025 detected - including all facturas and credit notes"
        );

        // Check which documents are already included
        const includedBsaleNumbers = new Set(
          allData.map((doc) => doc.bsale_number?.toString()).filter(Boolean)
        );

        // For June 2025, we want to include ALL facturas and credit notes that meet the date criteria
        // No need for hard-coded lists - let the normal filtering logic handle everything
        console.log(
          "✅ June 2025: All facturas and credit notes will be included through normal filtering"
        );
      }

      // Special handling for July 2025 credit notes - fetch them specifically (only for July, not later months)
      if (
        hasDateFilters &&
        startDate &&
        endDate &&
        startDate.getFullYear() === 2025 &&
        endDate.getFullYear() === 2025 &&
        startDate.getMonth() === 6 &&
        endDate.getMonth() === 6
      ) {
        console.log(
          "🔍 July 2025 detected - checking for specific credit notes"
        );

        // Check if we already have the July credit notes in our data
        const includedBsaleNumbers = new Set(
          allData.map((doc) => doc.bsale_number?.toString()).filter(Boolean)
        );

        const julyCreditNoteNumbers = ["10636", "10637", "10594"];
        const missingJulyCreditNotes = julyCreditNoteNumbers.filter(
          (num) => !includedBsaleNumbers.has(num)
        );

        console.log(
          `🔍 Missing July credit notes: ${JSON.stringify(missingJulyCreditNotes)}`
        );

        if (missingJulyCreditNotes.length > 0) {
          // Fetch the missing July credit notes specifically
          const julyCreditNotesQuery = supabase
            .from("sales_request_documents")
            .select(
              `*,
               sales_request:sales_request_id(
                 id,
                 client_id,
                 total_amount,
                 price_with_iva,
                 status,
                 paid,
                 paid_amount,
                 deleted_at,
                 payment_due_date,
                 factura_creation_date,
                 sales_condition_id,
                 created_by,
                 sales_conditions:sales_condition_id(name)
               )`
            )
            .in("bsale_number", missingJulyCreditNotes)
            .eq("document_type", "nota_de_credito")
            .is("deleted_at", null);

          // Note: Don't apply seller filtering for these specific July credit notes
          // They should be visible to all users as per business requirements

          const { data: julyCreditNotes, error: julyCreditNotesError } =
            await julyCreditNotesQuery;

          if (
            !julyCreditNotesError &&
            julyCreditNotes &&
            julyCreditNotes.length > 0
          ) {
            console.log(
              `✅ Found ${julyCreditNotes.length} July credit notes to include:`,
              julyCreditNotes.map((cn) => cn.bsale_number)
            );
            allData = [...allData, ...julyCreditNotes];
          } else if (julyCreditNotesError) {
            console.error(
              "❌ Error fetching July credit notes:",
              julyCreditNotesError
            );
          } else {
            console.log("⚠️ No July credit notes found in database");
          }
        } else {
          console.log("✅ All July credit notes already included");
        }
      }

      // Special handling for July 2025+ SII-declared facturas - fetch soft-deleted documents if needed
      const rangeIncludesJuly2025OrLater =
        hasDateFilters &&
        startDate &&
        endDate &&
        ((startDate.getFullYear() === 2025 && startDate.getMonth() >= 6) ||
          startDate.getFullYear() > 2025);

      if (rangeIncludesJuly2025OrLater) {
        console.log(
          "🔍 July 2025+ detected - checking for soft-deleted SII-declared facturas"
        );

        // Check if we already have the SII-declared facturas in our data
        const includedBsaleNumbers = new Set(
          allData.map((doc) => doc.bsale_number?.toString()).filter(Boolean)
        );

        const siiDeclaredNumbers = ["236884", "236883"];
        const missingSiiDeclaredNumbers = siiDeclaredNumbers.filter(
          (num) => !includedBsaleNumbers.has(num)
        );

        if (missingSiiDeclaredNumbers.length > 0) {
          console.log(
            `🔍 Missing SII-declared facturas for July 2025+: ${missingSiiDeclaredNumbers.join(", ")}`
          );

          // Fetch the missing SII-declared facturas, including soft-deleted ones
          let siiFacturasQuery = supabase
            .from("sales_request_documents")
            .select(
              `*,
               sales_request:sales_request_id(
                 id,
                 client_id,
                 total_amount,
                 price_with_iva,
                 status,
                 paid,
                 paid_amount,
                 deleted_at,
                 payment_due_date,
                 factura_creation_date,
                 sales_condition_id,
                 created_by,
                 sales_conditions:sales_condition_id(name),
                 clients:client_id(
                   id,
                   name,
                   is_test,
                   sales_condition_id,
                   sales_conditions:sales_condition_id(name)
                 )
               )`
            )
            .in("bsale_number", missingSiiDeclaredNumbers)
            .eq("document_type", "factura");
          // Note: We don't filter by deleted_at here to allow soft-deleted documents

          // Apply seller filtering if needed
          if (
            userRole === "seller" &&
            currentUserId &&
            sellerRequestIds.length > 0
          ) {
            if (sellerRequestIds.length <= 100) {
              siiFacturasQuery = siiFacturasQuery
                .in("sales_request_id", sellerRequestIds)
                .eq("is_temporary", false);
            } else {
              siiFacturasQuery = siiFacturasQuery.eq("is_temporary", false);
            }
          } else if (userRole !== "superadmin") {
            siiFacturasQuery = siiFacturasQuery.eq("is_temporary", false);
          }

          const { data: siiFacturasRaw, error: siiFacturasError } =
            await siiFacturasQuery
              .order("bsale_number", { ascending: false, nullsFirst: false })
              .order("created_at", { ascending: false });

          // Filter by seller on client side if needed
          let siiFacturas = siiFacturasRaw;
          if (
            userRole === "seller" &&
            currentUserId &&
            sellerRequestIds.length > 100 &&
            siiFacturasRaw
          ) {
            const sellerRequestIdsSet = new Set(sellerRequestIds);
            siiFacturas = siiFacturasRaw.filter((doc) =>
              sellerRequestIdsSet.has(doc.sales_request_id)
            );
            console.log(
              `🔍 SII facturas filtered by seller: ${siiFacturasRaw.length} -> ${siiFacturas.length}`
            );
          }

          if (!siiFacturasError && siiFacturas && siiFacturas.length > 0) {
            console.log(
              `✅ Found ${siiFacturas.length} SII-declared facturas to include for July 2025+:`,
              siiFacturas.map((f) => f.bsale_number)
            );
            allData = [...allData, ...siiFacturas];
          } else if (siiFacturasError) {
            console.error(
              "❌ Error fetching SII-declared facturas:",
              siiFacturasError
            );
          } else {
            console.log("⚠️ No SII-declared facturas found in database");
          }
        } else {
          console.log(
            "✅ All SII-declared facturas already included for July 2025+"
          );
        }
      }
    }

    // Get unique sales request IDs to fetch payments
    // Use Set to deduplicate in case both factura and credit note share the same sales_request_id
    const allSalesRequestIds = allData
      .map((doc) => (doc as any).sales_request?.id)
      .filter(Boolean);
    const salesRequestIds = Array.from(new Set(allSalesRequestIds));

    // Calculate effective paid amounts excluding checks in portfolio
    // This applies the same logic as get_sales_request_paid_amount but in batches for better performance
    console.log(
      `📊 Calculating paid amounts for ${salesRequestIds.length} sales requests`
    );
    const paymentsMap = new Map<string, number>();
    if (salesRequestIds.length > 0) {
      const batchSize = 100; // Process in batches of 100

      for (let i = 0; i < salesRequestIds.length; i += batchSize) {
        const batch = salesRequestIds.slice(i, i + batchSize);

        const { data: payments, error: paymentsError } = await supabase
          .from("payments")
          .select(
            "sales_request, amount, payment_method, check_status, created_at"
          )
          .in("sales_request", batch);

        if (!paymentsError && payments) {
          // First, deduplicate payments that have the same sales_request, amount, and timestamp
          const uniquePayments = new Map<string, any>();
          payments.forEach((payment) => {
            if (payment.sales_request) {
              // Create a unique key based on sales_request, amount, and timestamp
              const paymentKey = `${payment.sales_request}_${payment.amount}_${payment.created_at}`;

              // Only keep the first occurrence of each unique payment
              if (!uniquePayments.has(paymentKey)) {
                uniquePayments.set(paymentKey, payment);
              }
            }
          });

          // Sum payments per sales request, excluding checks in portfolio
          uniquePayments.forEach((payment) => {
            if (payment.sales_request) {
              // Apply the same logic as get_sales_request_paid_amount:
              // Include all non-check payments OR check payments that are collected/paid
              const shouldInclude =
                payment.payment_method !== "check" ||
                (payment.payment_method === "check" &&
                  payment.check_status &&
                  ["collected", "paid"].includes(payment.check_status));

              if (shouldInclude) {
                const currentTotal =
                  paymentsMap.get(payment.sales_request) || 0;
                const newTotal = currentTotal + (payment.amount || 0);
                paymentsMap.set(payment.sales_request, newTotal);
              }
            }
          });
        } else if (paymentsError) {
          console.error("Error fetching payments batch:", paymentsError);
        }
      }

      const totalBatches = Math.ceil(salesRequestIds.length / batchSize);
      console.log(
        `✅ Processed ${salesRequestIds.length} sales requests in ${totalBatches} batches instead of ${salesRequestIds.length} individual RPC calls`
      );
    }

    // Create a set of factura IDs from the current period for cross-period credit note filtering
    const currentPeriodFacturaIds = new Set<string>();
    allData.forEach((doc) => {
      if (doc.document_type === "factura" || doc.type === "factura") {
        currentPeriodFacturaIds.add(doc.id);
      }
    });

    // Transform to frontend format using our mapping function
    const documents: SalesRequestDocument[] = allData.filter((rawDoc) => {
      // Filter out documents from soft-deleted sales requests
      const salesRequest = (rawDoc as any).sales_request;
      const bsaleNumber = rawDoc.bsale_number?.toString();
      const documentType = rawDoc.document_type;

      // Debug for seller filtering
      if (
        userRole === "seller" &&
        (rawDoc.is_temporary === true || !salesRequest)
      ) {
        console.log(
          `🚫 SELLER FILTER BLOCKING DOC: bsale_number=${bsaleNumber}, is_temporary=${rawDoc.is_temporary}, has_sales_request=${!!salesRequest}, document_type=${documentType}`
        );
        if (!salesRequest) {
          console.log(
            `  ❌ No sales request found for document ${bsaleNumber}`
          );
        }
        if (rawDoc.is_temporary === true) {
          console.log(`  ❌ BSale Directo document ${bsaleNumber}`);
        }
      }

      // Special case: Always include specific facturas for May 2025 reporting
      if (hasDateFilters && startDate && endDate) {
        const rangeIncludes2025 =
          startDate.getFullYear() === 2025 && endDate.getFullYear() === 2025;

        const rangeIncludesMay2025 =
          rangeIncludes2025 &&
          startDate.getMonth() <= 4 &&
          endDate.getMonth() >= 4; // May is month 4

        const rangeIncludesJune2025 =
          rangeIncludes2025 &&
          startDate.getMonth() <= 5 &&
          endDate.getMonth() >= 5; // June is month 5

        // For June 2025, include ALL facturas (no hard-coded filtering)
        if (rangeIncludesJune2025) {
          // For sellers, only include if they created the sales request
          if (userRole === "seller" && currentUserId) {
            const isSellerOwned =
              salesRequest &&
              !salesRequest.deleted_at &&
              salesRequest.created_by === currentUserId;
            console.log(
              `🔒 SELLER CHECK: Factura ${bsaleNumber} - owned by seller: ${isSellerOwned}, sales_request.created_by: ${salesRequest?.created_by}`
            );
            return isSellerOwned;
          }
          console.log(
            `✅ ADMIN ACCESS: Including factura ${bsaleNumber} for non-seller user`
          );
          return salesRequest && !salesRequest.deleted_at;
        }

        // Include specific facturas for July 2025 ONLY (SII-declared facturas 236884 and 236883)
        // These facturas were generated in July 2025 and should only appear in July reports
        const rangeIncludesJuly2025Only =
          rangeIncludes2025 &&
          startDate.getMonth() <= 6 &&
          endDate.getMonth() >= 6 && // July is month 6
          startDate.getMonth() === 6 &&
          endDate.getMonth() === 6; // Only for July 2025, not later months

        if (
          rangeIncludesJuly2025Only &&
          ["236884", "236883"].includes(bsaleNumber)
        ) {
          console.log(
            `✅ Including July 2025-only SII-declared factura ${bsaleNumber} for July reporting`
          );
          return salesRequest && !salesRequest.deleted_at;
        }

        // For June 2025, include ALL credit notes (no hard-coded filtering)
        if (rangeIncludesJune2025 && documentType === "nota_de_credito") {
          console.log(
            `🔍 JUNE 2025: Including credit note ${bsaleNumber} for user ${currentUserId} (${userRole})`
          );
          // For sellers, only include if they created the sales request
          if (userRole === "seller" && currentUserId) {
            return (
              salesRequest &&
              !salesRequest.deleted_at &&
              salesRequest.created_by === currentUserId
            );
          }
          return salesRequest && !salesRequest.deleted_at;
        }

        if (
          rangeIncludesMay2025 &&
          (bsaleNumber === "232104" || bsaleNumber === "231827")
        ) {
          // For sellers, only include if they created the sales request
          if (userRole === "seller" && currentUserId) {
            return (
              salesRequest &&
              !salesRequest.deleted_at &&
              salesRequest.created_by === currentUserId
            );
          }
          return salesRequest && !salesRequest.deleted_at;
        }
      }

      // For credit notes, apply cross-period filtering logic
      if (documentType === "nota_de_credito") {
        // Handle standalone credit notes (created for BSale facturas)
        if (!salesRequest && rawDoc.is_temporary === true) {
          // For sellers, exclude BSale Directo credit notes (they should only see their own)
          if (userRole === "seller") {
            return false;
          }
          return true;
        }

        // Basic status check for regular credit notes
        const basicCheck =
          salesRequest &&
          !salesRequest.deleted_at &&
          // Exclude test clients
          !(salesRequest as any).clients?.is_test &&
          [
            "accepted",
            "delivered",
            "in_transit",
            "pending",
            "pending_pricing",
            "pending_credit",
            "dispatch_ready",
            "prepaid",
            "payment_to_check",
            "rejected",
            // Note: "test" status is now included in exports
          ].includes(salesRequest.status);

        if (!basicCheck) {
          return false;
        }

        // Special handling for specific months
        if (hasDateFilters && startDate && endDate) {
          const isFilteringMay2025 =
            startDate.getFullYear() === 2025 &&
            endDate.getFullYear() === 2025 &&
            startDate.getMonth() === 4 &&
            endDate.getMonth() === 4;

          const isFilteringJune2025 =
            startDate.getFullYear() === 2025 &&
            endDate.getFullYear() === 2025 &&
            startDate.getMonth() === 5 &&
            endDate.getMonth() === 5;

          // For July 2025 and later, include credit notes created in the same month
          const isFilteringJuly2025OrLater =
            (startDate.getFullYear() === 2025 && startDate.getMonth() >= 6) ||
            startDate.getFullYear() > 2025;

          if (isFilteringJuly2025OrLater) {
            // Check if the credit note was actually created in the same month as the filter
            const documentDate = new Date(rawDoc.created_at);
            const documentYear = documentDate.getFullYear();
            const documentMonth = documentDate.getMonth();
            const filterYear = startDate.getFullYear();
            const filterMonth = startDate.getMonth();

            const isFromSameMonth =
              documentYear === filterYear && documentMonth === filterMonth;

            console.log(
              `July 2025+ credit note filtering: ${bsaleNumber} - Created: ${documentDate.toISOString()}, Filter: ${filterYear}-${filterMonth + 1}, Document: ${documentYear}-${documentMonth + 1}, Including: ${isFromSameMonth}`
            );
            return isFromSameMonth;
          }

          // For May 2025, EXCLUDE ALL credit notes
          if (isFilteringMay2025) {
            console.log(
              `May 2025 credit note filtering: ${bsaleNumber} - Excluded (no credit notes allowed in May)`
            );
            return false;
          }

          // For June 2025, ONLY include the specific credit notes
          // if (isFilteringJune2025) {
          //   const allowedJune2025CreditNotes = [
          //     "10566",
          //     "10569",
          //     "10567",
          //     "10473",
          //   ];
          //   const isAllowedJune2025 =
          //     allowedJune2025CreditNotes.includes(bsaleNumber);
          //   console.log(
          //     `June 2025 credit note filtering: ${bsaleNumber} - Allowed: ${isAllowedJune2025}`
          //   );
          //   return isAllowedJune2025;
          // }
        }

        // Check if credit note references a factura from a different period
        const originalDocumentId = rawDoc.original_document_id;
        const creditNoteType = rawDoc.credit_note_type;

        if (originalDocumentId) {
          // For partial and administrative credit notes, always include them
          // (they represent real financial adjustments that should be reflected)
          if (
            creditNoteType === "partial" ||
            creditNoteType === "administrative"
          ) {
            console.log(
              `Credit note ${rawDoc.bsale_number}: ${creditNoteType} credit note - always included`
            );
            return true;
          }

          // For full credit notes, use cross-period logic
          const isOriginalFromCurrentPeriod =
            currentPeriodFacturaIds.has(originalDocumentId);
          const isCrossPeriod = !isOriginalFromCurrentPeriod;

          console.log(
            `Credit note ${rawDoc.bsale_number}: Full credit note - Original factura ${originalDocumentId} in current period: ${isOriginalFromCurrentPeriod}, Cross-period: ${isCrossPeriod}`
          );

          return isCrossPeriod;
        } else {
          // If no original_document_id, this might be an old credit note format, include it
          console.log(
            `Credit note ${rawDoc.bsale_number}: No original_document_id found, including in report`
          );
          return true;
        }
      }

      // For July 2025+, also include specific facturas (SII-declared facturas)
      if (
        documentType === "factura" &&
        hasDateFilters &&
        startDate &&
        endDate
      ) {
        const isFilteringJuly2025Only =
          startDate.getFullYear() === 2025 &&
          startDate.getMonth() === 6 &&
          endDate.getMonth() === 6;

        if (
          isFilteringJuly2025Only &&
          ["236884", "236883"].includes(bsaleNumber)
        ) {
          console.log(
            `July 2025 factura filtering: ${bsaleNumber} - Including SII-declared factura for July 2025 only`
          );
          return salesRequest && !salesRequest.deleted_at;
        }
      }

      // Always exclude test factura 236885
      if (bsaleNumber === "236885") {
        return false;
      }

      // For debit notes, apply similar logic to facturas - include if valid sales request
      if (documentType === "nota_de_debito") {
        return (
          salesRequest &&
          !salesRequest.deleted_at &&
          // Exclude test clients
          !(salesRequest as any).clients?.is_test &&
          [
            "accepted",
            "delivered",
            "in_transit",
            "pending",
            "pending_pricing",
            "pending_credit",
            "dispatch_ready",
            "prepaid",
            "payment_to_check",
            "rejected",
            // Note: "test" status is intentionally excluded from exports
          ].includes(salesRequest.status)
        );
      }

      // Special case: Include specific July credit notes but respect seller filtering
      if (
        documentType === "nota_de_credito" &&
        ["10636", "10637"].includes(bsaleNumber)
      ) {
        // For sellers, only include if it's from their sales request
        if (userRole === "seller" && currentUserId) {
          return (
            salesRequest &&
            !salesRequest.deleted_at &&
            salesRequest.created_by === currentUserId
          );
        }
        return salesRequest && !salesRequest.deleted_at;
      }

      // Include facturas from all valid sales request statuses (exclude only soft-deleted and test clients)
      const shouldInclude =
        salesRequest &&
        !salesRequest.deleted_at &&
        // Exclude test clients
        !(salesRequest as any).clients?.is_test &&
        [
          "accepted",
          "delivered",
          "in_transit",
          "pending",
          "pending_pricing",
          "pending_credit",
          "dispatch_ready",
          "prepaid",
          "payment_to_check",
          "rejected",
          // Note: "test" status is intentionally excluded from exports
        ].includes(salesRequest.status);

      return shouldInclude;
    });

    const processedDocuments: SalesRequestDocument[] = allData.map((rawDoc) => {
      const document = mapToSalesRequestDocument(rawDoc);
      const salesRequest = (rawDoc as any).sales_request;

      // Add client ID and amounts from the joined sales_request table
      document.clientId = salesRequest?.client_id;

      // Add client name and tax ID from the joined clients table
      const clientData = (salesRequest as any)?.clients;
      if (clientData) {
        (document as any).client_name = clientData.name;
        (document as any).client_tax_id = clientData.rut || clientData.tax_id;
      }

      // Check if this factura should have negative amounts for June 2025 reporting
      const bsaleNumber = rawDoc.bsale_number?.toString();
      const documentType = rawDoc.document_type;

      // For standalone credit notes, adjust client info since they have no sales request
      if (
        !salesRequest &&
        rawDoc.is_temporary === true &&
        documentType === "nota_de_credito"
      ) {
        console.log(
          `Handling standalone credit note ${bsaleNumber}: No sales request, using BSale data`
        );
        // Use client_id from database if available, otherwise undefined
        document.clientId = rawDoc.client_id || undefined;
      }
      const isJune2025CancelledFactura =
        hasDateFilters &&
        startDate &&
        endDate &&
        startDate.getFullYear() === 2025 &&
        endDate.getFullYear() === 2025 &&
        startDate.getMonth() === 5 &&
        endDate.getMonth() === 5 &&
        ["232104", "231827", "235700"].includes(bsaleNumber);

      // Add financial data (handle standalone credit notes and special cases)
      // Facturas exentas (SII 34) carry no IVA, so gross = net. The document's is_exempt
      // flag (from the sales_request_documents row) tells us; price_with_iva is exempt-blind.
      const totalAmount = salesRequest?.total_amount || 0;
      const priceWithIva = resolveGross({
        price_with_iva: salesRequest?.price_with_iva,
        total_amount: salesRequest?.total_amount,
        is_exento: (rawDoc as any).is_exempt,
      });

      // Handle credit notes - ALL credit notes should be negative
      if (documentType === "nota_de_credito") {
        console.log(
          `💰 Making amounts negative for credit note ${bsaleNumber} (all credit notes are negative)`
        );

        if (!salesRequest && rawDoc.is_temporary === true) {
          // Standalone credit notes (created for BSale facturas)
          const netAmount = -Math.abs(rawDoc.net_amount || 0); // Make negative
          const taxAmount = -Math.abs(rawDoc.tax_amount || 0); // Make negative
          const docTotalAmount = -Math.abs(
            rawDoc.total_amount || Math.abs(netAmount)
          ); // Make negative

          (document as any).total_amount = docTotalAmount;
          (document as any).price_with_iva = netAmount + taxAmount;
        } else {
          // Regular credit notes - use document's net amounts, make them negative
          const netAmount = -Math.abs(rawDoc.net_amount || 0); // Make negative
          const taxAmount = -Math.abs(rawDoc.tax_amount || 0); // Make negative
          (document as any).total_amount = netAmount; // Use net amount for credit notes
          (document as any).price_with_iva = netAmount + taxAmount; // Net + tax for gross amount
        }
      } else if (documentType === "nota_de_debito") {
        console.log(
          `💰 Making amounts positive for debit note ${bsaleNumber} (all debit notes are positive)`
        );

        // Debit notes should always be positive (they represent additional charges/corrections)
        const netAmount = Math.abs(rawDoc.net_amount || 0); // Ensure positive
        const taxAmount = Math.abs(rawDoc.tax_amount || 0); // Ensure positive
        const docTotalAmount = Math.abs(rawDoc.total_amount || 0); // Ensure positive

        (document as any).total_amount = docTotalAmount;
        (document as any).price_with_iva = netAmount + taxAmount;
      } else if (isJune2025CancelledFactura) {
        console.log(
          `💰 Keeping amounts positive for cancelled factura ${bsaleNumber} in June 2025 (credit notes will be negative)`
        );
        (document as any).total_amount = totalAmount;
        (document as any).price_with_iva = priceWithIva;
      } else if (["236883", "236884"].includes(bsaleNumber)) {
        // Special handling for SII-declared facturas 236883 and 236884
        // Use document amounts directly since they might not have proper sales request amounts
        const docTotalAmount = Math.abs(rawDoc.total_amount || 0);
        const docNetAmount = Math.abs(rawDoc.net_amount || 0);
        const docTaxAmount = Math.abs(rawDoc.tax_amount || 0);

        // Calculate price_with_iva from net + tax, or use existing value
        const calculatedPriceWithIva = docNetAmount + docTaxAmount;
        const finalPriceWithIva =
          calculatedPriceWithIva || Math.abs(priceWithIva) || docTotalAmount;
        const finalTotalAmount =
          docTotalAmount || finalPriceWithIva || Math.abs(totalAmount);

        (document as any).total_amount = finalTotalAmount;
        (document as any).price_with_iva = finalPriceWithIva;
      } else {
        (document as any).total_amount = totalAmount;
        (document as any).price_with_iva = priceWithIva;
      }

      // Calculate actual paid amount from payments
      const calculatedPaidAmount = salesRequest?.id
        ? paymentsMap.get(salesRequest.id) || 0
        : 0;
      (document as any).paid_amount = calculatedPaidAmount;

      // Add sales request metadata
      (document as any).sales_request_status = salesRequest?.status;
      (document as any).sales_request_paid = salesRequest?.paid;
      (document as any).payment_due_date = salesRequest?.payment_due_date;
      (document as any).factura_creation_date =
        salesRequest?.factura_creation_date;
      // Use sales request's specific condition if available, otherwise fall back to client's default condition
      const salesRequestCondition = salesRequest?.sales_conditions?.name;
      const clientCurrentCondition = (salesRequest as any)?.clients
        ?.sales_conditions?.name;
      (document as any).sales_condition_name =
        salesRequestCondition || clientCurrentCondition;

      // Add factura emission date from the document itself (stored as bigint timestamp)
      (document as any).emission_date = rawDoc.emission_date;

      // Add bsale_number for Excel export
      (document as any).bsale_number = rawDoc.bsale_number;

      // Add document's own amount fields for credit notes
      (document as any).net_amount = rawDoc.net_amount;
      (document as any).tax_amount = rawDoc.tax_amount;
      (document as any).document_total_amount = rawDoc.total_amount;

      return document;
    });

    return processedDocuments;
  } catch (error) {
    console.error("Error in fetchAllFacturas:", error);
    return [];
  }
};
